/* Görsel katmanları — tek kaynak.
   Yükleme hattı (tarayıcı), üretici (generator) ve Worker aynı sayıları
   buradan okur. Bir katmanı değiştirmek isterseniz burayı değiştirin;
   türevler orijinallerden yeniden üretilebilir. */

/** Üretilen her türev katmanı. */
export const TIERS = ['browse', 'full', 'orig'] as const;
export type Tier = (typeof TIERS)[number];

/**
 * Gezinme katmanı: ızgara, karusel ve albüm şeridi bunu yükler.
 * Herkese açık ve taranabilir — Google Görseller trafiği buradan gelir.
 */
export const BROWSE = {
  /* DİKKAT: bu bir GENİŞLİK sınırıdır, uzun kenar sınırı değil.
     Gezinme katmanı works.html'deki mozaik ızgarada durur; orada kareyi
     sınırlayan boyut genişliktir. Uzun kenarı sınırlarsak 2:3 bir portre
     600x900'e düşer — bugünkü 900x1350'nin her kenarda %33 altı, retina
     ekranda gözle görülür bir yumuşama. Mevcut site de 900'ü genişlik
     olarak kullanıyor (data/works.json: w=900, h=1350). */
  maxWidth: 900,
  mime: 'image/webp',
  ext: 'webp',
  quality: 0.72,
  /* Gerçek karelerle ölçüldü (bkz. tools/harness).
     Bu stüdyonun işi grenli siyah-beyaz; gren gürültüsü bit hızını ele
     geçiriyor ve sıkışmıyor. ed26-2 karesinde kalite eğrisi neredeyse
     düz: q0,60 → 122 KB, q0,76 → 154 KB. Yani kaliteyi düşürmek kareyi
     yumuşatıyor ama bayt kazandırmıyor — doğru hamle bandı gerçeğe
     genişletmek. Karşılaştırma: aynı karenin bugünkü JPEG'i 146 KB. */
  targetBytes: { min: 70_000, max: 160_000 }
} as const;

/**
 * Büyütme katmanı: yalnızca kareye tıklanınca yüklenir.
 *
 * 1800 px, hırsızlığa karşı alınmış bilinçli bir karardır (plan §5.2).
 * 2x ekranda ~900 CSS px genişliğinde gösterilen bir büyütme katmanı için
 * fazlasıyla yeterli — yani hiçbir ekranda kalite kaybı görünmez — ama
 * ciddi bir baskı boyutu için yetersizdir. Gönderilmeyen piksel çalınamaz.
 */
export const FULL = {
  /* Burada uzun kenar doğru ölçüdür: büyütme katmanı görüntü alanına
     sığdırılır, yani dikey karelerde yüksekliği sınırlamak gerekir. */
  maxEdge: 1800,
  mime: 'image/jpeg',
  ext: 'jpg',
  quality: 0.8,
  targetBytes: { min: 350_000, max: 700_000 }
} as const;

/** Mobilde bellek dostu ara katman (plan §15 risk maddesi). */
export const FULL_MOBILE = {
  maxEdge: 1200,
  mime: 'image/jpeg',
  ext: 'jpg',
  quality: 0.78
} as const;

/** Yüklenirken bulanık zemin — Postgres'te base64 olarak durur. */
export const LQIP = {
  maxEdge: 24,
  mime: 'image/webp',
  quality: 0.5,
  /** Bu boyutu aşarsa satır şişer; üretimde uyarı verilir. */
  maxBytes: 1_200
} as const;

/** İmzalı büyütme adreslerinin ömrü. Kopyalanan adres bu süre sonunda ölür. */
export const MEDIA_TOKEN_TTL_SECONDS = 120;

/** Büyütme katmanında bir kare açılırken komşularından kaçı hazırlanır. */
export const PREFETCH_RADIUS = 1;

/** Filigran biçimi — albüm başına seçilir. */
export const WATERMARK_STYLES = ['corner', 'tiled', 'none'] as const;
export type WatermarkStyle = (typeof WATERMARK_STYLES)[number];

/** Köşe filigranının yerleşimi; uzun kenara oranla ölçeklenir. */
export const WATERMARK = {
  text: 'FRAWGRAPHY',
  /** Uzun kenarın yüzdesi olarak kenar boşluğu. */
  insetRatio: 0.03,
  /** Uzun kenarın yüzdesi olarak yazı yüksekliği. */
  sizeRatio: 0.022,
  opacity: 0.55,
  /** Açık karelerde de okunması için gölge. */
  shadow: 'rgba(0,0,0,0.45)'
} as const;

/** Herkese açık türevlere gömülen hak bildirimi (§5.2 adım 4). */
export const IPTC_RIGHTS = {
  creator: 'Frawgraphy',
  copyrightNotice: (year: number) => `© ${year} Frawgraphy — Tüm hakları saklıdır.`,
  usageTerms: 'https://frawgraphy.com/telif',
  webStatement: 'https://frawgraphy.com/telif'
} as const;

/** R2 anahtarları içerik adreslidir; kare değişirse yeni id doğar. */
export const key = {
  browse: (id: string) => `browse/${id}.${BROWSE.ext}`,
  full: (id: string) => `full/${id}.${FULL.ext}`,
  /* Dar ekranlara giden daha küçük büyütme katmanı. Yükleme hattı bu katmanı
     henüz üretmiyor (derive() tek bir `full` döndürüyor); Worker nesneyi
     bulamazsa `full`e düşer. Hat bu katmanı üretmeye başlayınca düşüş
     kendiliğinden devre dışı kalır. */
  mobile: (id: string) => `mobile/${id}.${FULL_MOBILE.ext}`,
  orig: (id: string, ext: string) => `orig/${id}.${ext.replace(/^\./, '').toLowerCase()}`,
  grant: (grantId: string, id: string, ext: string) =>
    `grants/${grantId}/${id}.${ext.replace(/^\./, '').toLowerCase()}`
} as const;

/**
 * Orijinal olarak kabul edilen uzantılar — `key.orig` anahtarının son parçası.
 *
 * Kapalı bir küme olmasının sebebi anahtarın kendisidir: `orig/<id>.<ext>`
 * içinde uzantı, istemciden gelen tek serbest metindir. Serbest bırakılırsa
 * çağıran taraf anahtarı biçimlendirmeye başlar. Hangi biçimin tarayıcıda
 * çözülebildiğinin kaynağı packages/imaging/intake.ts'tir; bu liste onun
 * kabul ettiği tiplerin uzantı karşılığıdır.
 */
export const ORIGINAL_EXTS = ['jpg', 'jpeg', 'png', 'webp', 'avif'] as const;
export type OriginalExt = (typeof ORIGINAL_EXTS)[number];

/**
 * Genişliği `maxWidth`'e sığdırır — mozaik ızgara için doğru ölçü.
 * Küçük kareler büyütülmez.
 */
export function fitWidth(
  w: number,
  h: number,
  maxWidth: number
): { width: number; height: number } {
  if (w <= maxWidth) return { width: w, height: h };
  return { width: maxWidth, height: Math.round((h * maxWidth) / w) };
}

/**
 * Uzun kenarı `maxEdge`'e sığdıran ölçek — en/boy oranı korunur.
 * Küçük kareler büyütülmez (upscale yok).
 */
export function fitLongEdge(
  w: number,
  h: number,
  maxEdge: number
): { width: number; height: number } {
  const longest = Math.max(w, h);
  if (longest <= maxEdge) return { width: w, height: h };
  const scale = maxEdge / longest;
  return { width: Math.round(w * scale), height: Math.round(h * scale) };
}
